import api from "api/axios";

function attendanceBase() {
  return localStorage.getItem("authUserType") === "admin" ? "/admin" : "/employee";
}

export interface AttendanceRecord {
  id: number;
  date: string;
  timestamp: string;
  employee_code: string;
  employee_name: string;
  status: number;
  status_label: string;
  serial_number: string;
  is_manually_modified: boolean;
  modified_action?: "add" | "edit" | "delete";
  modified_by?: string;
  modified_at?: string;
}

// A single raw punch as embedded in a day summary — everything AttendanceRecord
// has except date/employee_code/employee_name, which live on the day summary
// itself (see toAttendanceRecord below for recombining them).
export type AttendancePunch = Omit<AttendanceRecord, "date" | "employee_code" | "employee_name">;

export interface AttendanceDaySummary {
  employee_code: string;
  employee_name: string;
  date: string;
  check_in: AttendancePunch | null;
  check_out: AttendancePunch | null;
  duration_minutes: number | null;
  completeness: "complete" | "missing_check_out" | "missing_check_in" | "absent";
  has_multiple_check_in: boolean;
  has_multiple_check_out: boolean;
  is_weekend: boolean;
  is_late: boolean;
  late_by_minutes: number | null;
  // True only on a completeness=="absent" day covered by an APPROVED leave
  // request — if the employee still punched in that day, the real punches
  // win and this stays false (see attendance_db._fetch_approved_leave_days).
  is_leave: boolean;
  leave_type: "annual" | "sick" | "casual" | "unpaid" | "other" | null;
  all_punches: AttendancePunch[];
}

// Recombines a day summary + one of its punches into the flat shape
// AttendanceActionModal (built for single-punch add/edit/delete) expects.
export function toAttendanceRecord(day: AttendanceDaySummary, punch: AttendancePunch): AttendanceRecord {
  return { ...punch, date: day.date, employee_code: day.employee_code, employee_name: day.employee_name };
}

export interface AttendanceUpdateRequestItem {
  id: number;
  employee_code: string;
  attendance_date: string;
  action: "add" | "edit" | "delete";
  target_record_id: number | null;
  requested_status: number | null;
  requested_timestamp: string | null;
  reason: string;
  status: "pending" | "approved" | "rejected";
  requested_by: string;
  created_at: string;
  reviewed_at: string | null;
  review_note: string | null;
}

export interface AttendanceEmployee {
  id: number;
  employee_code: string;
  name: string;
}

// Computed over the full date-range + employee scope, before entry_status/
// late_only trim the table's rows — a stable summary of the current
// selection, not of whichever drill-down chip happens to be active. The
// fields from on_time_rate onward are only populated when scoped to exactly
// one employee (a single row picked in the multi-select, or an employee
// viewing their own attendance).
export interface AttendanceStats {
  total_days: number;
  complete: number;
  missing_check_out: number;
  missing_check_in: number;
  absent: number;
  late: number;
  // Weekend-excluded like every other stat here — a Fri-Mon leave request
  // is 2 workdays taken, not 4 calendar days. Only meaningful when scoped
  // to one employee (see on_time_rate below) — 0 otherwise, since the
  // whole leave lookup is skipped outside that scope.
  leave: number;
  on_time_rate: number | null;
  avg_arrival_time_label: string | null;
  avg_duration_minutes: number | null;
  streak_type: "late" | "on_time" | null;
  streak_count: number;
}

export interface AttendanceEntryStatusFilter {
  value: "" | "complete" | "missing_check_out" | "missing_check_in" | "absent" | "multiple";
  label: string;
}

export const ENTRY_STATUS_FILTERS: AttendanceEntryStatusFilter[] = [
  { value: "", label: "All" },
  { value: "complete", label: "Complete" },
  { value: "missing_check_out", label: "Missing Check-Out" },
  { value: "missing_check_in", label: "Missing Check-In" },
  { value: "absent", label: "Absent" },
  { value: "multiple", label: "Multiple Entries" },
];

export async function fetchAttendanceRecords(
  page = 1,
  pageSize = 50,
  startDate?: string,
  endDate?: string,
  employeeCodes?: string[],
  entryStatus?: string,
  lateOnly?: boolean,
) {
  const params: Record<string, any> = { page, page_size: pageSize };
  if (startDate) params.start_date = startDate;
  if (endDate) params.end_date = endDate;
  if (employeeCodes && employeeCodes.length > 0) params.employee_codes = employeeCodes.join(",");
  if (entryStatus) params.entry_status = entryStatus;
  if (lateOnly) params.late_only = true;
  const res = await api.get(`${attendanceBase()}/attendance_records`, { params });
  return res.data as {
    items: AttendanceDaySummary[];
    total_count: number;
    page: number;
    total_pages: number;
    truncated: boolean;
    stats: AttendanceStats;
  };
}

export async function fetchAttendanceEmployees(): Promise<AttendanceEmployee[]> {
  const res = await api.get(`${attendanceBase()}/attendance/employees`);
  return res.data;
}

export async function fetchAttendanceDayDetail(employeeCode: string, date: string): Promise<AttendanceDaySummary> {
  const res = await api.get(`${attendanceBase()}/attendance/day`, { params: { employee_code: employeeCode, day: date } });
  return res.data;
}

export async function adminAddRecord(employeeCode: string, status: number, timestamp: string, note?: string) {
  const res = await api.post("/admin/attendance_records", { employee_code: employeeCode, status, timestamp, note });
  return res.data;
}

export async function adminEditRecord(id: number, status: number, timestamp: string, note?: string) {
  const res = await api.patch(`/admin/attendance_records/${id}`, { status, timestamp, note });
  return res.data;
}

export async function adminDeleteRecord(id: number, note?: string) {
  const res = await api.delete(`/admin/attendance_records/${id}`, { params: note ? { note } : undefined });
  return res.data;
}

export async function submitUpdateRequest(body: {
  action: "add" | "edit" | "delete";
  employee_code: string;
  attendance_date: string;
  target_record_id?: number;
  requested_status?: number;
  requested_timestamp?: string;
  reason: string;
}) {
  const res = await api.post("/employee/attendance/update-requests", body);
  return res.data;
}

export async function fetchMyUpdateRequests(status?: string, page = 1, pageSize = 50) {
  const params: Record<string, any> = { page, page_size: pageSize };
  if (status) params.status = status;
  const res = await api.get("/employee/attendance/update-requests", { params });
  return res.data;
}

// No pagination UI yet, so pageSize defaults to the backend's max (200, see
// attendance.py's Query(le=200)) rather than 50 — org-wide, the admin view
// can plausibly exceed 50 pending update requests.
export async function fetchAllUpdateRequests(status?: string, employeeCode?: string, page = 1, pageSize = 200) {
  const params: Record<string, any> = { page, page_size: pageSize };
  if (status) params.status = status;
  if (employeeCode) params.employee_code = employeeCode;
  const res = await api.get("/admin/attendance/update-requests", { params });
  return res.data;
}

export async function reviewUpdateRequest(id: number, decision: "approve" | "reject", reviewNote?: string) {
  const res = await api.patch(`/admin/attendance/update-requests/${id}`, { decision, review_note: reviewNote });
  return res.data;
}

export async function fetchAuditLog(employeeCode?: string, startDate?: string, endDate?: string, page = 1, pageSize = 50) {
  const params: Record<string, any> = { page, page_size: pageSize };
  if (employeeCode) params.employee_code = employeeCode;
  if (startDate) params.start_date = startDate;
  if (endDate) params.end_date = endDate;
  const res = await api.get("/admin/attendance/audit-log", { params });
  return res.data;
}
